import Vue from 'vue'
import axios from 'axios'

/**
 * One shared state for account push notifications (the signed-in user's own push, not the
 * anonymous visitor push), read by every instance that shows it: the page panels
 * (PushNotificationsPanel), the enable/disable button (NotificationsSubscribe) and the bell
 * dropdown control (PushDisableControl). Enabling or disabling in one place updates them all.
 *
 * status:
 *  - 'loading'      : not known yet - nothing is shown, so the large panel never flashes;
 *  - 'unsupported'  : no service worker / push in this browser;
 *  - 'denied'       : the browser blocks notifications for this site;
 *  - 'unsubscribed' : this browser has no push subscription registered for the user;
 *  - 'subscribed'   : this browser has a push subscription AND the server has it registered.
 *
 * Notification.permission === 'granted' alone is NOT 'subscribed': the subscription itself is
 * read from pushManager.getSubscription() and registered on the server (an idempotent upsert of
 * this browser's endpoint). JavaScript cannot revoke the browser's site permission; disabling
 * removes this browser's subscription and its server registration only - other devices,
 * notification history and email preferences are untouched.
 */
export const pushState = Vue.observable({
  status: 'loading',
  busy: false,
  error: null, // null | 'enable_failed' | 'disable_failed' | 'sync_failed'
})

const defaultEnv = () => ({
  navigator: typeof navigator !== 'undefined' ? navigator : undefined,
  window: typeof window !== 'undefined' ? window : undefined,
  http: axios,
  // The layouts declare it as `const beamsClient` in a classic script: a global binding, but
  // not a window property, so it is read by name.
  // eslint-disable-next-line no-undef
  beamsClient: () => (typeof beamsClient !== 'undefined' ? beamsClient : undefined),
})

let env = defaultEnv()
let resolving = null
let watching = false

/** Tests swap the browser/HTTP dependencies; called with no argument it restores the defaults. */
export function configurePushEnvironment(overrides) {
  env = overrides ? { ...defaultEnv(), ...overrides } : defaultEnv()
  resolving = null
  watching = false
  pushState.status = 'loading'
  pushState.busy = false
  pushState.error = null
}

/** Beams is loaded for signed-in visitors only; anywhere else this half is skipped. */
function beams() {
  const w = env.window || {}
  const client = env.beamsClient ? env.beamsClient() : undefined
  if (!client || !w.PusherPushNotifications || !(w.Laravel && w.Laravel.user)) {
    return null
  }
  return { client, PusherPushNotifications: w.PusherPushNotifications, userId: w.Laravel.user.id }
}

function supported() {
  const nav = env.navigator
  const w = env.window || {}
  return !!(nav && nav.serviceWorker && w.PushManager && w.ServiceWorkerRegistration
    && 'showNotification' in w.ServiceWorkerRegistration.prototype)
}

function permission() {
  const w = env.window || {}
  return w.Notification ? w.Notification.permission : 'default'
}

async function registration() {
  await env.navigator.serviceWorker.register('/service-worker.js')
  return env.navigator.serviceWorker.ready
}

function encodeKey(subscription, name) {
  const key = subscription.getKey ? subscription.getKey(name) : null
  return key ? env.window.btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null
}

/** Registers this browser's subscription on the server (insert or update by endpoint). */
function saveOnServer(subscription) {
  const w = env.window
  return env.http.post('/push-subscriptions', {
    endpoint: subscription.endpoint,
    publicKey: encodeKey(subscription, 'p256dh'),
    authToken: encodeKey(subscription, 'auth'),
    contentEncoding: ((w.PushManager && w.PushManager.supportedContentEncodings) || ['aesgcm'])[0],
  })
}

/** Starts the account-side Beams device for this browser (signed-in pages only). */
async function startBeams() {
  const b = beams()
  if (!b) return
  const provider = new b.PusherPushNotifications.TokenProvider({ url: '/api/pusher/beams-auth' })
  await b.client.start()
  await b.client.setUserId(`${b.userId}`, provider)
}

async function stopBeams() {
  const b = beams()
  if (!b) return
  await b.client._deleteDevice()
  await b.client.stop()
}

/**
 * Works out the current state. Shared: concurrent callers get the same promise. Pass
 * { force: true } to look again (permission changed, tab became visible).
 */
export function resolvePushState({ force = false } = {}) {
  if (resolving && !force) {
    return resolving
  }
  resolving = (async () => {
    if (!supported()) {
      pushState.status = 'unsupported'
      return pushState.status
    }
    if (permission() === 'denied') {
      pushState.status = 'denied'
      return pushState.status
    }
    try {
      const reg = await registration()
      const subscription = await reg.pushManager.getSubscription()
      if (!subscription) {
        pushState.status = 'unsubscribed'
        return pushState.status
      }
      try {
        await saveOnServer(subscription)
        pushState.status = 'subscribed'
        if (pushState.error === 'sync_failed') pushState.error = null
      } catch (e) {
        // The browser is subscribed but the server does not have it: offer enabling again
        // (which re-registers) rather than claim notifications will arrive.
        pushState.status = 'unsubscribed'
        pushState.error = 'sync_failed'
      }
    } catch (e) {
      pushState.status = 'unsupported'
    }
    watchForChanges()
    return pushState.status
  })()
  return resolving
}

/** Permission or subscription can change outside the app (browser settings, another tab). */
function watchForChanges() {
  if (watching) return
  watching = true
  const nav = env.navigator
  if (nav && nav.permissions && nav.permissions.query) {
    nav.permissions.query({ name: 'notifications' })
      .then((status) => { status.onchange = () => resolvePushState({ force: true }) })
      .catch(() => {})
  }
  const doc = typeof document !== 'undefined' ? document : null
  if (doc && doc.addEventListener) {
    doc.addEventListener('visibilitychange', () => {
      if (doc.visibilityState === 'visible' && !pushState.busy) resolvePushState({ force: true })
    })
  }
}

/** Subscribes this browser and registers it. Resolves to true on success. */
export async function enablePush() {
  if (pushState.busy) return false
  pushState.busy = true
  pushState.error = null
  try {
    const reg = await registration()
    const options = { userVisibleOnly: true }
    const vapid = env.window.Laravel && env.window.Laravel.vapidPublicKey
    if (vapid) options.applicationServerKey = urlBase64ToUint8Array(vapid, env.window)
    const subscription = await reg.pushManager.subscribe(options)
    await saveOnServer(subscription)
    pushState.status = 'subscribed'
    try {
      await startBeams()
    } catch (e) {
      // Account-side Beams device is a second channel; the web push subscription stands.
      console.error('Beams start failed', e)
    }
    return true
  } catch (e) {
    if (permission() === 'denied') {
      pushState.status = 'denied'
    } else {
      pushState.error = 'enable_failed'
    }
    return false
  } finally {
    pushState.busy = false
  }
}

/**
 * Disables push for THIS browser only, in an order that never leaves a silent half state:
 *  1. remove this browser's registration on the server (by endpoint; other devices stay);
 *  2. remove this browser's Beams device;
 *  3. unsubscribe this browser.
 * If a later step fails, the earlier ones are undone (re-register / restart Beams) and the
 * state stays 'subscribed' with an error, so the action can simply be retried.
 */
export async function disablePush() {
  if (pushState.busy) return false
  pushState.busy = true
  pushState.error = null
  let subscription = null
  let serverRemoved = false
  let beamsStopped = false
  try {
    const reg = await registration()
    subscription = await reg.pushManager.getSubscription()
    if (!subscription) {
      pushState.status = 'unsubscribed'
      return true
    }
    await env.http.post('/push-subscriptions/delete', { endpoint: subscription.endpoint })
    serverRemoved = true
    await stopBeams()
    beamsStopped = true
    const unsubscribed = await subscription.unsubscribe()
    if (unsubscribed === false) {
      throw new Error('unsubscribe returned false')
    }
    pushState.status = 'unsubscribed'
    return true
  } catch (e) {
    pushState.error = 'disable_failed'
    try {
      if (beamsStopped) await startBeams()
      if (serverRemoved && subscription) await saveOnServer(subscription)
    } catch (restoreError) {
      // Could not restore: the next resolvePushState() reports the real state.
      pushState.status = 'unsubscribed'
      return false
    }
    pushState.status = 'subscribed'
    return false
  } finally {
    pushState.busy = false
  }
}

export function urlBase64ToUint8Array(base64String, w = window) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = w.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
